import type { ValidationSchema } from 'forms';
import type {
  AttendanceSettingsFormState,
  AttendanceSettingsTabs,
  HoursPerDayMode,
  OvertimeFormula,
} from 'components/Settings/AttendanceAndLeavesSettings/types';
import {
  AttendanceSettingsResponse,
  CustomHolidayInput,
  HolidaySelection,
  UpdateAttendanceSettingsRequest,
} from 'api/types/attendanceSettingsTypes';
import { CHECK_IN_OUT_TIME } from 'utils/regexes';
import { FORMULA_UNIT_DISPLAY, TAB_ROUTES } from './constants';
import type { OtSalaryComponent } from './PayRulesTab/components/OvertimeSection/hooks/useOtSalaryComponents';
import type { BulkUpdateOtBehaviourComponentInput } from 'api/gql-utils/overtime/types';

// Pad a 1- or 2-digit number to "HH" or "MM".
const pad2 = (n: number): string => (n < 10 ? `0${n}` : `${n}`);

// Convert minutes (e.g. 535) to a "HH:MM" string (e.g. "08:55").
export const formatMinutesAsTime = (minutes: number): string => {
  const safe = Math.max(0, Math.round(minutes));
  const h = Math.floor(safe / 60);
  const m = safe % 60;
  return `${pad2(h)}:${pad2(m)}`;
};

// Parse "HH:MM" into total minutes. Returns null for malformed input.
export const parseTimeToMinutes = (value: string | null | undefined): number | null => {
  if (!value) return null;
  const match = /^(\d{1,2}):(\d{2})$/.exec(value);
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (h > 23 || m > 59) return null;
  return h * 60 + m;
};

// Round minutes down to the nearest 5 (used for "Recommended" shift defaults).
export const roundToNearestFive = (minutes: number): number => Math.floor(minutes / 5) * 5;

// Compute the shift length in minutes, handling overnight shifts.
export const getShiftDurationInMinutes = (
  start?: string | null,
  end?: string | null,
): number | null => {
  const s = parseTimeToMinutes(start);
  const e = parseTimeToMinutes(end);
  if (s === null || e === null) return null;
  // Overnight shift: end is on the next day.
  return e >= s ? e - s : 24 * 60 - s + e;
};

// ---------- weekend / cadence helpers ----------

// True when both shift start and end are populated.
export const areShiftTimingsComplete = (
  shiftStart: string | null,
  shiftEnd: string | null,
): boolean => Boolean(shiftStart) && Boolean(shiftEnd);

// ---------- holiday formatting ----------

// Convert ISO YYYY-MM-DD to "DD/MM/YYYY" for display. Returns input on bad data.
export const formatHolidayDate = (iso: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return iso;
  const [, y, m, d] = match;
  return `${d}/${m}/${y}`;
};

// Render the "Applies To" cell — either an "All" badge or the list.
export const describeHolidayScope = (selectedLocations: string[]): string =>
  selectedLocations.length === 0 ? 'All' : selectedLocations.join(', ');

export const attendanceSettingsValidation: ValidationSchema<AttendanceSettingsFormState> = {
  shiftStart: (form) => {
    if (!form.isShiftTimingEnabled) return null;
    if (!form.shiftStart) return 'Shift start time is required';
    if (!CHECK_IN_OUT_TIME.test(form.shiftStart)) return 'Use HH:MM format (e.g. 09:00)';
    return null;
  },

  shiftEnd: (form) => {
    if (!form.isShiftTimingEnabled) return null;
    if (!form.shiftEnd) return 'Shift end time is required';
    if (!CHECK_IN_OUT_TIME.test(form.shiftEnd)) return 'Use HH:MM format (e.g. 18:00)';
    return null;
  },

  leaveTypes: (form) => {
    if (!form.leaveTypes || form.leaveTypes.length === 0) {
      return 'At least one leave type is required';
    }
    const names = form.leaveTypes.map((t) => t.name.trim().toLowerCase());
    const hasDuplicates = new Set(names).size !== names.length;
    if (hasDuplicates) return 'Leave type names must be unique';
    const hasEmpty = form.leaveTypes.some((t) => !t.name.trim());
    if (hasEmpty) return 'Leave type name cannot be empty';
    return null;
  },
};

export function deriveHoursPerDayMode(
  baseHours: string | null | undefined,
): HoursPerDayMode | undefined {
  if (baseHours === '8' || baseHours === '9') return baseHours;
  if (baseHours != null) return 'custom';
  return undefined;
}

export function buildRequest(
  form: Partial<AttendanceSettingsFormState>,
): UpdateAttendanceSettingsRequest {
  // Build general — conditionally include fields gated by visibility flags.

  const general: UpdateAttendanceSettingsRequest['general'] = {
    isAttendanceEnabled: form.isAttendanceEnabled!,
    enableContractorsAttendance: form.enableContractorsAttendance!,
    showAttendanceOnPayslip: form.showAttendanceOnPayslip!,
    calculateLopOnWorkingDays: form.calculateLopOnWorkingDays!,
    allowNegativeLeaves: form.allowNegativeLeaves!,
    allowHalfdays: form.allowHalfdays!,
    compulsoryLeaveRemarks: form.compulsoryLeaveRemarks!,
    useFinancialYear: form.useFinancialYear!,
  };

  if (form.autoAttendanceLopSettingVisible) {
    general.autoAttendanceLop = form.autoAttendanceLop;
  }

  if (form.hasBiometricDevices) {
    general.useWebAttendanceWithBiometric = form.useWebAttendanceWithBiometric;
  }

  // Build holiday selections from predefined holidays.
  const selections: HolidaySelection[] =
    form.predefinedHolidays?.map((h) => ({
      holidayId: h.id,
      locations: h.selectedLocations,
    })) || [];

  // Build custom holidays.
  const customHolidays: CustomHolidayInput[] =
    form.customHolidays?.map((h) => ({
      date: h.date,
      description: h.description,
    })) || [];

  return {
    general,
    weekend: {
      sun: form.weekendSun || false,
      sat1: form.weekendSat1 || false,
      sat2: form.weekendSat2 || false,
      sat3: form.weekendSat3 || false,
      sat4: form.weekendSat4 || false,
      sat5: form.weekendSat5 || false,
    },
    leaveTypes: form.leaveTypes || [],
    shift: {
      isShiftTimingEnabled: form.isShiftTimingEnabled ?? false,
      shiftStart: form.shiftStart ?? null,
      shiftEnd: form.shiftEnd ?? null,
      gracePeriodForCheckin: form.gracePeriodForCheckin ?? null,
      minimumShiftDurationFullDay: form.minimumShiftDurationFullDay ?? null,
      minimumShiftDurationHalfDay: form.minimumShiftDurationHalfDay ?? null,
    },
    holidays: {
      year: form.holidayYear ?? new Date().getFullYear(),
      selections,
      customHolidays,
    },
    payRules: {
      baseHours: form.baseHours ?? null,
    },
  };
}

export function buildFormState(
  data: AttendanceSettingsResponse['data'],
): AttendanceSettingsFormState {
  const { general, weekend, shift, leaveTypes, holidays, visibility } = data;

  return {
    // General
    isAttendanceEnabled: general.isAttendanceEnabled,
    enableContractorsAttendance: general.enableContractorsAttendance,
    showAttendanceOnPayslip: general.showAttendanceOnPayslip,
    autoAttendanceLop: general.autoAttendanceLop,
    calculateLopOnWorkingDays: general.calculateLopOnWorkingDays,
    allowNegativeLeaves: general.allowNegativeLeaves,
    allowHalfdays: general.allowHalfdays,
    compulsoryLeaveRemarks: general.compulsoryLeaveRemarks,
    useFinancialYear: general.useFinancialYear,
    useWebAttendanceWithBiometric: general.useWebAttendanceWithBiometric,

    // Weekend
    weekendSun: weekend.sun,
    weekendSat1: weekend.sat1,
    weekendSat2: weekend.sat2,
    weekendSat3: weekend.sat3,
    weekendSat4: weekend.sat4,
    weekendSat5: weekend.sat5,

    // Shift
    isShiftTimingEnabled: shift.isShiftTimingEnabled,
    shiftStart: shift.shiftStart,
    shiftEnd: shift.shiftEnd,
    gracePeriodForCheckin: shift.gracePeriodForCheckin,
    minimumShiftDurationFullDay: shift.minimumShiftDurationFullDay,
    minimumShiftDurationHalfDay: shift.minimumShiftDurationHalfDay,

    // Leave types
    leaveTypes,

    // Holidays
    predefinedHolidays: holidays.predefined,
    customHolidays: holidays.custom,
    holidayYear: holidays.year,
    holidayLocations: holidays.locations,

    // Visibility
    autoAttendanceLopSettingVisible: visibility.autoAttendanceLopSettingVisible,
    hasBiometricDevices: visibility.hasBiometricDevices,
    allowDecimalMonthlyIncrement: visibility.allowDecimalMonthlyIncrement,
    showLocations: visibility.showLocations,

    // Pay Rules — from API, with defaults for unconfigured orgs
    baseHours: data.payRules?.baseHours ?? null,
    baseHoursInDb: data.payRules?.baseHours ?? null,
  };
}

export const getActiveTabFromPath = (pathname: string): AttendanceSettingsTabs => {
  if (pathname.includes(TAB_ROUTES['pay-rules'])) return 'pay-rules';
  if (pathname.includes(TAB_ROUTES.leaves)) return 'leaves';
  return 'attendance';
};

export const buildFormulaDescription = (
  formula: OvertimeFormula,
  otComponents: OtSalaryComponent[],
): string => {
  if (formula.rateType === 'flat') {
    return `₹${Number(formula.flatAmount ?? 0) / 100} per ${FORMULA_UNIT_DISPLAY[formula.unit]}`;
  }
  const included = otComponents.filter((c) => c.includeInOtWages);
  const componentStr =
    included.length > 0 ? included.map((c) => c.name).join(' + ') : 'No components';
  const base = `${formula.multiplier}× (${componentStr}) per ${FORMULA_UNIT_DISPLAY[formula.unit]}`;
  if (formula.dailyAllowance) {
    return `${base} + ₹${Number(formula.dailyAllowance ?? 0) / 100}/day`;
  }
  return base;
};

// Diff selected OT salary components against their current includeInOtWages state,
// returning only the components whose flag needs to change.
export const computeOtComponentChanges = (
  otComponents: OtSalaryComponent[],
  selectedComponentIds: string[],
): BulkUpdateOtBehaviourComponentInput[] => {
  const additions = selectedComponentIds
    .filter((id) => {
      const comp = otComponents.find((c) => c.id === id);
      return comp && !comp.includeInOtWages;
    })
    .map((id) => {
      const component = otComponents.find((c) => c.id === id)!;
      return {
        componentId: id,
        includeInOtWages: true,
        componentSource: component.componentSource,
      };
    });

  const removals = otComponents
    .filter((c) => c.includeInOtWages && !selectedComponentIds.includes(c.id))
    .map((c) => ({
      componentId: c.id,
      includeInOtWages: false,
      componentSource: c.componentSource,
    }));

  return [...additions, ...removals];
};
